import { ChangeDetectionStrategy, Component, computed, input, output, Signal } from '@angular/core';
import { OverviewCardComponent } from '@iot-platform/iot-platform-ui';
import { OverviewCard, OverviewCardActions } from '@iot-platform/models/common';
import { Site } from '@iot-platform/models/oyan';

export enum SiteCardActions {
  EDIT = 'EDIT',
  DELETE = 'DELETE'
}

@Component({
  selector: 'oyan-ui-site-card',
  imports: [OverviewCardComponent],
  templateUrl: './site-card.component.html',
  styleUrls: ['./site-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteCardComponent {
  canUpdate = input<boolean>(false);
  canDelete = input<boolean>(false);
  site = input<Site>();
  loading = input<boolean>(false);

  update = output<void>();
  delete = output<void>();

  card: Signal<OverviewCard>;

  constructor() {
    this.card = computed(() => {
      const site = this.site();
      return site ? this.getCardMetadata(site) : null;
    });
  }

  onDispatchCardEvent(actionType: SiteCardActions): void {
    if (actionType === SiteCardActions.EDIT) {
      this.update.emit();
    } else if (actionType === SiteCardActions.DELETE) {
      this.delete.emit();
    }
  }

  private getCardMetadata(site: Site) {
    const canUpdate = this.canUpdate();
    const canDelete = this.canDelete();
    const width = '50%';

    return {
      name: site?.displayName,
      justifyContent: true,
      image: site?.imageUrl,
      defaultImage: 'assets/images/site/site_default.png',
      actions: [
        {
          label: 'SITES.CARD.EDIT',
          type: `${SiteCardActions.EDIT}` as OverviewCardActions,
          disabled: !canUpdate
        },
        {
          label: 'SITES.CARD.DELETE',
          type: `${SiteCardActions.DELETE}` as OverviewCardActions,
          disabled: !canDelete || site?.totalAssets !== 0 || site?.totalDevices !== 0
        }
      ],
      sections: [
        {
          title: 'IOT_DICTIONARY.GENERAL',
          collapsible: true,
          expanded: true,
          properties: [
            { label: 'SITES.CARD.SHIP_TO_ID', value: site?.name, width },
            { label: 'SITES.CARD.TYPE', value: `SITES.CARD.TYPES.${site?.type}`, width },
            { label: 'SITES.CARD.ENTITY', value: site?.entity?.name, width },
            { label: 'SITES.TABLE_CONTENT.INFRASTRUCTURE_PROVIDER', value: site?.infraType, width },
            { label: 'IOT_DICTIONARY.DESCRIPTION', innerHTMLValue: site?.description, width: '100%', cssClass: 'flex-col' }
          ]
        },
        {
          title: 'IOT_DICTIONARY.LOCATION',
          collapsible: true,
          expanded: true,
          properties: [
            { label: 'SITES.INFO_FORM.ADDRESS_1', value: site?.address?.address1, width },
            { label: 'SITES.INFO_FORM.ADDRESS_2', value: site?.address?.address2, width },
            {
              label: 'IOT_DICTIONARY.ZIP_CODE',
              value: `${site.address?.city} ${site.address?.zipCode ? `(${site.address?.zipCode})` : ''}`,
              width
            },
            {
              label: 'IOT_DICTIONARY.COUNTRY',
              value: `IOT_DICTIONARY.COUNTRIES.${site.address?.country}`,
              width
            }
          ]
        }
      ]
    };
  }
}
